import {type FC, type RefObject, useCallback, useEffect, useMemo, useReducer, useRef, useState} from 'react';
import type {JitsiTrackState} from '../types/lib-jitsi-meet';
import useStreamingStore from '../../../store/useStreamingStore.ts';
import {
    blueTeam,
    redTeam,
    getMappingTable,
    evaluateTrackPriority,
    isVideoPlaying,
    isVideoVisiblyDark,
    isVideoTrackActive,
    MONITOR_BOT_NAME,
    STUDIO_NAME,
    shouldReceiveByName,
    type ILayOut,
} from '../../../utils/StreamingUtil.ts';
import CenterWait from '../../../assets/broadcast/center-wait.jpg';

interface RemoteCellProps {
    remoteElemRef: RefObject<Array<HTMLVideoElement>>;
    remoteTracks: RefObject<Map<string, JitsiTrackState>>;
    remoteTrackVersion?: number;
    remoteSize: number;
    gridInfo: ILayOut;
    excludedParticipants?: RefObject<Set<string>>;
    displayedParticipants?: RefObject<Set<string>>;
    selectedByStudio?: RefObject<Set<string>>;
    selectedByStudioOrder?: RefObject<Array<string>>;
    excludedByStudio?: RefObject<Set<string>>;
    studioStateVersion?: number;
    isWatching?: boolean;
    onConstraintUpdate?: () => void;
}

interface TrackPriority {
    trackId: string;
    trackState: JitsiTrackState;
    priority: number;
}

interface CachedPriority {
    priority: number;
    lastUpdated: number;
    lastVideoCheck: number;
}

const PRIORITY_CACHE_DURATION = 15000;
const VIDEO_CHECK_INTERVAL = 40000;
const ROTATION_INTERVAL = 20000;
const STABILIZATION_PERIOD = 40000;
const STUDIO_SELECTED_PRIORITY = 1000;
const tempCenterName = [
    '아트윈푸르지오 경로당',
    '동부아파트 경로당',
    '이편한세상부평역어반루체 경로당',
    '그린스퀘어 경로당',
    '다남동 경로당',
    '동인천역파크푸르지오 경로당',
    '금호어울림1차 경로당',
    '작전1동경로당',
    '논현하늘마을4단지 경로당',
    '에코메트로11단지 경로당',
    '삼산신원아침도시A 경로당',
    '경남2차A 경로당',
    '삼산타운1단지 경로당',
    '부개역코오롱하늘채A 경로당',
    '쌍용A 경로당',
    '대림A 경로당',
    '갈산주공2차A 경로당',
    '부평 남부 경로당',
];

const RemoteCell: FC<RemoteCellProps> = ({remoteElemRef, remoteTracks, remoteTrackVersion = 0, remoteSize, gridInfo, excludedParticipants, displayedParticipants, selectedByStudio, selectedByStudioOrder, excludedByStudio, studioStateVersion = 0, isWatching = false, onConstraintUpdate}) => {
    const {state, gridLayout} = useStreamingStore();
    const rotationIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const remoteCellId = useRef<Array<string>>(Array(remoteSize).fill(''));
    const hiddenTrackMaps = useRef<Map<string, number>>(new Map());
    const priorityCache = useRef<Map<string, CachedPriority>>(new Map());
    const [, forceUpdate] = useReducer((x) => x + 1, 0);
    const isFirstAttachRef = useRef(true);
    const isStabilizingRef = useRef(true);

    // remoteCellId → displayedParticipants 동기화
    const syncDisplayedParticipants = useCallback(() => {
        if (!displayedParticipants?.current) return;
        displayedParticipants.current.clear();
        for (const id of remoteCellId.current) {
            if (id) displayedParticipants.current.add(id);
        }
    }, [displayedParticipants]);

    const getDisplayName = useCallback(
        (trackId: string): string => {
            if (!trackId) return '-';
            // merge 맵에 각인된 displayName 우선 (봇 conf의 partId는 origin conf로 해석 불가)
            const stamped = remoteTracks.current?.get(trackId)?.displayName;
            if (stamped) return stamped;
            if (!state.conference) return '-';
            return state.conference.getParticipantById(trackId)?.getDisplayName() ?? '-';
        },
        [state.conference, remoteTracks]
    );

    const remoteTracksArray = useMemo(() => {
        if (!remoteTracks.current) return [];
        console.log('전체 접속자 수: ', remoteTracks.current.size);
        return Array.from(remoteTracks.current.entries());
    }, [remoteTracks.current?.size, remoteTrackVersion]);

    const getBasePriority = useCallback((trackState: JitsiTrackState): number => {
        let priority = 0;
        // hidden 트랙은 실제 화면 상태를 알 수 없으므로 정상 그리드 셀(150)보다 낮게 책정
        if (trackState.videoTrack) {
            priority += 130;
        }
        if (trackState.audioTrack) {
            priority += 10;
        }
        if (trackState.videoTrack === null) {
            priority -= 200;
        }
        return priority;
    }, []);

    const getVideoBonus = useCallback((trackState: JitsiTrackState, videoElement: HTMLVideoElement): number => {
        if (!videoElement || !trackState.videoTrack) return 0;
        let bonus = 0;
        if (isVideoPlaying(videoElement)) {
            bonus += 100;
        }
        if (!isVideoVisiblyDark(videoElement)) {
            bonus += 50;
        }
        return bonus;
    }, []);

    // 캐쉬에서 가져오기
    const getCachedPriority = useCallback(
        (trackId: string, trackState: JitsiTrackState, videoElement?: HTMLVideoElement): number => {
            const now = Date.now();
            const cached = priorityCache.current.get(trackId);

            // 캐쉬
            if (cached && now - cached.lastUpdated < PRIORITY_CACHE_DURATION) {
                if (videoElement && now - cached.lastVideoCheck > VIDEO_CHECK_INTERVAL) {
                    const videoBonus = getVideoBonus(trackState, videoElement);
                    const basePriority = getBasePriority(trackState);
                    const newPriority = basePriority + videoBonus;
                    priorityCache.current.set(trackId, {
                        priority: newPriority,
                        lastUpdated: cached.lastUpdated,
                        lastVideoCheck: now,
                    });
                    return newPriority;
                }
                return cached.priority;
            }

            // 노 캐쉬
            const priority = evaluateTrackPriority(trackId, trackState, videoElement);
            priorityCache.current.set(trackId, {
                priority,
                lastUpdated: now,
                lastVideoCheck: now,
            });
            return priority;
        },
        []
    );

    const getSortedTracks = useCallback((): Array<TrackPriority> => {
        if (!remoteTracks.current || !remoteElemRef.current) return [];
        const tracksWithPriority: Array<TrackPriority> = [];
        const currentlyDisplayed = new Set(remoteCellId.current.filter((id) => id));
        const studioBan = excludedByStudio?.current;
        const studioSelected = selectedByStudio?.current;
        const studioOrder = selectedByStudioOrder?.current;
        // studio selected 배열 순서 → 인덱스 맵 (앞쪽일수록 더 높은 priority)
        const orderIndex = new Map<string, number>();
        if (studioOrder) {
            studioOrder.forEach((id, idx) => orderIndex.set(id, idx));
        }
        for (const [trackId, trackState] of remoteTracksArray) {
            // 봇/스튜디오 트랙은 그리드 후보 아님 (방어적 2중 체크)
            const name = trackState.displayName ?? '';
            if (name === MONITOR_BOT_NAME || name === STUDIO_NAME) continue;
            // studio가 ban한 partId는 후보 풀에서 완전 제외 — 자동 평가/로테이션 대상 아님
            if (studioBan?.has(trackId)) continue;

            let priority;
            // studio selected는 최상위 우선순위. 배열 순서가 앞일수록 priority 더 높음
            if (studioSelected?.has(trackId)) {
                const idx = orderIndex.get(trackId) ?? 0;
                priority = STUDIO_SELECTED_PRIORITY - idx;
            } else if (hiddenTrackMaps.current.has(trackId)) {
                priority = getBasePriority(trackState);
                hiddenTrackMaps.current.set(trackId, priority);
            } else if (currentlyDisplayed.has(trackId)) {
                const currentIndex = remoteCellId.current.findIndex((id) => id === trackId);
                const videoElement = currentIndex >= 0 ? remoteElemRef.current?.[currentIndex] : undefined;
                priority = getCachedPriority(trackId, trackState, videoElement);
            } else {
                priority = getCachedPriority(trackId, trackState);
            }
            tracksWithPriority.push({
                trackId,
                trackState,
                priority,
            });
        }
        return tracksWithPriority.sort((a, b) => b.priority - a.priority);
    }, [remoteTracksArray, getCachedPriority, getBasePriority, excludedByStudio, selectedByStudio, selectedByStudioOrder]);

    // ── 셀 렌더링 진단 로그 ──
    // 진단 전용: brightness 값을 직접 산출 (임계값 튜닝 근거 확보용)
    const measureBrightness = (videoEl: HTMLVideoElement): number | null => {
        if (!videoEl || videoEl.videoWidth === 0) return null;
        try {
            const canvas = document.createElement('canvas');
            canvas.width = 8;
            canvas.height = 8;
            const ctx = canvas.getContext('2d');
            if (!ctx) return null;
            ctx.drawImage(videoEl, 0, 0, 8, 8);
            const pixels = ctx.getImageData(0, 0, 8, 8).data;
            let total = 0;
            for (let i = 0; i < pixels.length; i += 4) {
                total += pixels[i] + pixels[i + 1] + pixels[i + 2];
            }
            return total / (8 * 8 * 3);
        } catch (e) {
            console.warn('measureBrightness 실패:', e);
            return null;
        }
    };

    const diagnoseCells = useCallback((label: string) => {
        if (!remoteElemRef.current) return;
        let noRef = 0, noTrackId = 0, noTrackState = 0, noVideoTrack = 0;
        let noSrcObject = 0, paused = 0, zeroSize = 0, dark = 0, playing = 0;
        const failedCells: string[] = [];
        const darkCells: string[] = [];
        const brightnessSamples: string[] = [];

        for (let i = 0; i < remoteSize; i++) {
            const trackId = remoteCellId.current[i];
            const videoEl = remoteElemRef.current?.[i];

            if (!videoEl) { noRef++; failedCells.push(`[${i}]ref없음`); continue; }
            if (!trackId) { noTrackId++; failedCells.push(`[${i}]trackId없음`); continue; }

            const trackState = remoteTracks.current?.get(trackId);
            if (!trackState) { noTrackState++; failedCells.push(`[${i}]trackState없음`); continue; }
            if (!trackState.videoTrack) { noVideoTrack++; failedCells.push(`[${i}]videoTrack없음`); continue; }

            const stream = videoEl.srcObject as MediaStream | null;
            if (!stream || stream.getVideoTracks().length === 0) {
                noSrcObject++;
                failedCells.push(`[${i}]srcObject없음(${getDisplayName(trackId)})`);
                continue;
            }

            if (videoEl.paused || videoEl.ended) {
                paused++;
                failedCells.push(`[${i}]paused(${getDisplayName(trackId)})`);
                continue;
            }

            if (videoEl.videoWidth === 0 || videoEl.videoHeight === 0) {
                zeroSize++;
                failedCells.push(`[${i}]0x0(${getDisplayName(trackId)})`);
                continue;
            }

            // 모든 1차 검사 통과 → brightness 측정
            const brightness = measureBrightness(videoEl);
            const brightnessStr = brightness === null ? 'N/A' : brightness.toFixed(1);
            brightnessSamples.push(`[${i}]${getDisplayName(trackId)}=${brightnessStr}`);

            if (isVideoVisiblyDark(videoEl)) {
                dark++;
                darkCells.push(`[${i}]${getDisplayName(trackId)}(b=${brightnessStr})`);
                continue;
            }

            playing++;
        }

        console.log(
            `🔍 [CELL진단:${label}] 총${remoteSize}셀 | ✅재생=${playing} | ` +
            `⬛검정=${dark} | ` +
            `❌ref없음=${noRef} trackId없음=${noTrackId} state없음=${noTrackState} ` +
            `video없음=${noVideoTrack} src없음=${noSrcObject} paused=${paused} 0x0=${zeroSize}`
        );
        if (brightnessSamples.length > 0) {
            console.log(`🔍 [CELL진단:${label}] brightness 샘플: ${brightnessSamples.join(', ')}`);
        }
        if (darkCells.length > 0) {
            console.warn(`🔍 [CELL진단:${label}] 검정 셀 상세: ${darkCells.join(', ')}`);
        }
        if (failedCells.length > 0) {
            console.warn(`🔍 [CELL진단:${label}] 실패 셀 상세: ${failedCells.join(', ')}`);
        }
    }, [remoteSize, getDisplayName]);

    const attachAllTracks = useCallback(() => {
        const sortedTracks = getSortedTracks();
        const newAssignments = Array(remoteSize).fill('');
        const usedTracks = new Set<string>();
        const isLargeGrid = remoteSize > 50;
        if (isLargeGrid) {
            // 대규모: own(짝)은 앞 절반 셀, bot(홀)은 뒤 절반 셀에 고정 배치한다.
            // priority 순으로만 채우면 own이 앞쪽 ~50칸을 독점해 origin이 50 한계에 빠듯해진다.
            // own/bot을 셀 위치로 분리하면 각 연결이 절반(~34)만 책임져 여유가 생기고 배치가 예측 가능.
            const ownBoundary = Math.ceil(remoteSize / 2); // 앞 절반 = own
            let ownCell = 0;
            let botCell = ownBoundary;
            for (const track of sortedTracks) {
                if (usedTracks.has(track.trackId)) continue;
                const name = track.trackState.displayName ?? '';
                if (shouldReceiveByName(name, 'own')) {
                    if (ownCell >= ownBoundary) continue; // own 칸 소진
                    newAssignments[ownCell++] = track.trackId;
                } else {
                    if (botCell >= remoteSize) continue; // bot 칸 소진
                    newAssignments[botCell++] = track.trackId;
                }
                usedTracks.add(track.trackId);
                hiddenTrackMaps.current.delete(track.trackId);
            }
        } else {
            for (let i = 0; i < Math.min(sortedTracks.length, remoteSize); i++) {
                const track = sortedTracks[i];
                if (!usedTracks.has(track.trackId)) {
                    newAssignments[i] = track.trackId;
                    usedTracks.add(track.trackId);
                    hiddenTrackMaps.current.delete(track.trackId);
                }
            }
        }
        remoteCellId.current = newAssignments;
        if (!remoteTracks.current || !remoteElemRef.current) return;
        remoteCellId.current.forEach((trackId, index) => {
            if (index >= remoteSize) return;
            const videoElement = remoteElemRef.current?.[index];
            const audioElement = document.getElementById(`remote-audio-${index}`);
            const trackState = trackId ? remoteTracks.current?.get(trackId) : undefined;
            if (trackState) {
                if (videoElement && trackState.videoTrack) {
                    trackState.videoTrack.attach(videoElement);
                }
                if (audioElement && trackState.audioTrack) {
                    trackState.audioTrack.attach(audioElement);
                }
            }
        });
        syncDisplayedParticipants();
        onConstraintUpdate?.();
        forceUpdate();

        // attach 직후 + 3초 후 진단 (비디오 재생 시작 대기)
        diagnoseCells('attach직후');
        setTimeout(() => diagnoseCells('attach+3초'), 3000);
    }, [getSortedTracks, remoteSize, syncDisplayedParticipants, onConstraintUpdate, diagnoseCells]);

    const detachAllTracks = useCallback(() => {
        if (!remoteTracks.current || !remoteElemRef.current) return;
        remoteTracksArray.forEach(([trackId, trackState]) => {
            for (let index = 0; index < remoteSize; index++) {
                const videoElement = remoteElemRef.current?.[index];
                const audioElement = document.getElementById(`remote-audio-${index}`);
                if (videoElement && trackState.videoTrack) {
                    trackState.videoTrack.detach(videoElement);
                }
                if (audioElement && trackState.audioTrack) {
                    trackState.audioTrack.detach(audioElement);
                }
            }
        });
        remoteCellId.current = Array(remoteSize).fill('');
        hiddenTrackMaps.current.clear();
        priorityCache.current.clear();
        forceUpdate();
    }, [remoteTracksArray, remoteSize]);

    const attachTrackToCell = (track: TrackPriority, cellIndex: number) => {
        const videoElement = remoteElemRef.current?.[cellIndex];
        const audioElement = document.getElementById(`remote-audio-${cellIndex}`);

        if (videoElement && track.trackState.videoTrack) {
            track.trackState.videoTrack.attach(videoElement);
        }
        if (audioElement && track.trackState.audioTrack) {
            track.trackState.audioTrack.attach(audioElement);
        }
        remoteCellId.current[cellIndex] = track.trackId;
        hiddenTrackMaps.current.delete(track.trackId);
        displayedParticipants?.current?.add(track.trackId);
        forceUpdate();
    };

    const detachTrackFromCell = (cellIndex: number) => {
        const trackId = remoteCellId.current[cellIndex];
        if (!trackId) return;
        const trackState = remoteTracks.current?.get(trackId);
        if (!trackState) return;
        const videoElement = remoteElemRef.current?.[cellIndex];
        const audioElement = document.getElementById(`remote-audio-${cellIndex}`);
        const priority = getCachedPriority(trackId, trackState, videoElement);
        hiddenTrackMaps.current.set(trackId, priority);
        if (videoElement && trackState.videoTrack) {
            trackState.videoTrack.detach(videoElement);
        }
        if (audioElement && trackState.audioTrack) {
            trackState.audioTrack.detach(audioElement);
        }
        remoteCellId.current[cellIndex] = '';
        displayedParticipants?.current?.delete(trackId);
        forceUpdate();
    };

    const rotationByPolicy = useCallback(() => {
        if (!remoteTracks.current || !remoteElemRef.current) return;

        // 1단계: 그리드 내 비활성/검정 셀 검출
        // 대규모 그리드(50+)는 JVB ramp-up 시간이 길어 안정화 전까지는 평가 skip
        // studio selected/ban 그룹은 studio가 통제하므로 자동 평가에서 제외
        const inactiveCells: Array<{index: number; trackId: string}> = [];
        if (!isStabilizingRef.current && remoteSize <= 50) {
            for (let i = 0; i < remoteSize; i++) {
                const trackId = remoteCellId.current[i];
                if (!trackId) continue;
                // studio가 selected/ban한 partId는 자동 평가 대상 아님
                if (selectedByStudio?.current?.has(trackId)) continue;
                if (excludedByStudio?.current?.has(trackId)) continue;
                const trackState = remoteTracks.current.get(trackId);
                if (!trackState) continue;
                const videoElement = remoteElemRef.current[i];
                if (!videoElement) continue;

                if (!isVideoTrackActive(trackState, videoElement)) {
                    inactiveCells.push({index: i, trackId});
                    // 검정/비활성 셀 캐시 즉시 무효화 (다음 평가 시 최신 상태 반영)
                    priorityCache.current.delete(trackId);
                    if (excludedParticipants?.current && !excludedParticipants.current.has(trackId)) {
                        console.log(`🚫 [EXCLUDE] ${getDisplayName(trackId)} (${trackId}) - 비활성 트랙`);
                        excludedParticipants.current.add(trackId);
                    }
                } else if (excludedParticipants?.current?.has(trackId)) {
                    console.log(`✅ [RESTORE] ${getDisplayName(trackId)} (${trackId}) - 트랙 복구`);
                    excludedParticipants.current.delete(trackId);
                }
            }
        }

        // 2단계: 교체용 hidden 트랙 풀 준비
        const allTracks = getSortedTracks();
        const validTracks = allTracks.filter((track) => track.priority > 0);
        const currentGridTrackIds = new Set(remoteCellId.current.filter((id) => id));
        const hiddenTracks = validTracks.filter(
            (track) => !currentGridTrackIds.has(track.trackId) && !excludedParticipants?.current?.has(track.trackId)
        );

        // 3단계 (Fast-path): 검정 셀이 있으면 hidden 트랙으로 즉시 교체
        if (inactiveCells.length > 0) {
            const calIndex = [-11, -10, -9, -1, 0, +1, +9, +10, 11];
            let replacedCount = 0;
            const usedHiddenIds = new Set<string>();

            for (const inactive of inactiveCells) {
                // 인접 셀과 중복되지 않는 hidden 트랙 찾기
                const candidate = hiddenTracks.find((track) => {
                    if (usedHiddenIds.has(track.trackId)) return false;
                    for (let i = 0; i < calIndex.length; i++) {
                        const neighborTrackId = remoteCellId.current[inactive.index + calIndex[i]];
                        if (neighborTrackId && neighborTrackId === track.trackId) return false;
                    }
                    return true;
                });

                if (!candidate) {
                    console.log(`⚠️ [DARK_REPLACE] ${getDisplayName(inactive.trackId)} 교체용 hidden 트랙 부족`);
                    continue;
                }

                console.log(
                    `🔄 [DARK_REPLACE] ${getDisplayName(inactive.trackId)} -> ${getDisplayName(candidate.trackId)} (${candidate.priority})`
                );
                excludedParticipants?.current?.delete(candidate.trackId);
                detachTrackFromCell(inactive.index);
                attachTrackToCell(candidate, inactive.index);
                usedHiddenIds.add(candidate.trackId);
                replacedCount++;
            }

            if (replacedCount > 0) {
                onConstraintUpdate?.();
            }
            return;
        }

        // 4단계: 모두 정상인 경우 — 기존 priority 비교 로직 (매 호출 재평가)
        if (validTracks.length === 0 || remoteTracksArray.length <= remoteSize) return;
        if (hiddenTracks.length === 0) return;

        const maxHiddenPriority = Math.max(...hiddenTracks.map((track) => track.priority));
        if (maxHiddenPriority <= 110) return;

        const currentGridTracks = remoteCellId.current
            .map((trackId, index) => {
                if (!trackId) return null;
                const trackState = remoteTracks.current?.get(trackId);
                if (!trackState) return null;
                const videoElement = remoteElemRef.current?.[index];
                const priority = getCachedPriority(trackId, trackState, videoElement);
                return {index, trackId, priority};
            })
            .filter((track): track is NonNullable<typeof track> => track !== null);
        if (currentGridTracks.length === 0) return;

        const minCurrentPriority = Math.min(...currentGridTracks.map((track) => track.priority));
        const lowestPriorityTracks = currentGridTracks.filter((track) => track.priority === minCurrentPriority);
        // hidden(130)이 정상 셀(150)보다 항상 낮으므로 이 분기는 모두 정상일 때 정상적으로 종료
        if (maxHiddenPriority <= minCurrentPriority + 20) {
            console.log('모두 정상 - 이번 회차 rotation skip');
            return;
        }

        const targetCell = lowestPriorityTracks[Math.floor(Math.random() * lowestPriorityTracks.length)];
        const bestHiddenTracks = hiddenTracks.filter((track) => track.priority === maxHiddenPriority);
        const selectedHiddenTrack = bestHiddenTracks[Math.floor(Math.random() * bestHiddenTracks.length)];
        let flag = true;
        const calIndex = [-11, -10, -9, -1, 0, +1, +9, +10, 11];
        console.log('대상 경로당 명: ', getDisplayName(selectedHiddenTrack.trackId));
        for (let i = 0; i < 9; i++) {
            if (remoteCellId.current[targetCell.index + calIndex[i]]) {
                console.log(
                    '계산 중인 경로당 명: ',
                    getDisplayName(remoteCellId.current[targetCell.index + calIndex[i]])
                );
                if (remoteCellId.current[targetCell.index + calIndex[i]] === selectedHiddenTrack.trackId) flag = false;
            }
        }
        if (flag) {
            console.log(
                `🔄 교체: ${getDisplayName(targetCell.trackId)} (${targetCell.priority}) -> ${getDisplayName(selectedHiddenTrack.trackId)} (${selectedHiddenTrack.priority})`
            );
            if (excludedParticipants?.current) {
                excludedParticipants.current.add(targetCell.trackId);
                excludedParticipants.current.delete(selectedHiddenTrack.trackId);
            }
            detachTrackFromCell(targetCell.index);
            attachTrackToCell(selectedHiddenTrack, targetCell.index);
            onConstraintUpdate?.();
        } else {
            console.log('targetCell 과 selectedHiddenTrack 이 가까이 있기 때문에 skip');
        }
    }, [getSortedTracks, getCachedPriority, getDisplayName, remoteTracksArray.length, remoteSize, remoteCellId, onConstraintUpdate, excludedParticipants]);

    const cleanupCache = useCallback(() => {
        const now = Date.now();
        const currentTrackIds = new Set(remoteTracksArray.map(([trackId]) => trackId));
        for (const [trackId, cached] of priorityCache.current.entries()) {
            if (!currentTrackIds.has(trackId) || now - cached.lastUpdated > PRIORITY_CACHE_DURATION * 3) {
                priorityCache.current.delete(trackId);
            }
        }
    }, [remoteTracksArray]);

    // ref 를 사용하여 interval/unmount 에서 최신 함수 참조 (interval 재시작 방지)
    const latestFnsRef = useRef({rotationByPolicy, cleanupCache, detachAllTracks});
    latestFnsRef.current = {rotationByPolicy, cleanupCache, detachAllTracks, diagnoseCells} as any;

    // 증분 트랙 부착: 기존 셀은 유지하고 새 참가자만 빈 셀에 배치
    const incrementalAttach = useCallback(() => {
        if (!remoteTracks.current || !remoteElemRef.current) return;

        const validTrackIds = new Set(remoteTracksArray.map(([id]) => id));

        // 1. 떠난 참가자의 셀 정리 + 제거된 트랙의 stale srcObject 정리
        for (let i = 0; i < remoteSize; i++) {
            const trackId = remoteCellId.current[i];
            if (!trackId) continue;
            if (!validTrackIds.has(trackId)) {
                const videoElement = remoteElemRef.current?.[i];
                const audioElement = document.getElementById(`remote-audio-${i}`) as HTMLMediaElement | null;
                if (videoElement) videoElement.srcObject = null;
                if (audioElement) audioElement.srcObject = null;
                remoteCellId.current[i] = '';
            } else {
                // 참가자는 있지만 트랙이 제거된 경우 stale srcObject 정리
                const trackState = remoteTracks.current?.get(trackId);
                if (trackState) {
                    const videoElement = remoteElemRef.current?.[i];
                    const audioElement = document.getElementById(`remote-audio-${i}`) as HTMLMediaElement | null;
                    if (videoElement && !trackState.videoTrack && videoElement.srcObject) {
                        videoElement.srcObject = null;
                    }
                    if (audioElement && !trackState.audioTrack && audioElement.srcObject) {
                        audioElement.srcObject = null;
                    }
                }
            }
        }

        // 2. 이미 할당된 셀에 새로 도착한 video/audio track re-attach
        for (let i = 0; i < remoteSize; i++) {
            const trackId = remoteCellId.current[i];
            if (!trackId) continue;
            const trackState = remoteTracks.current?.get(trackId);
            if (!trackState) continue;
            const videoElement = remoteElemRef.current?.[i];
            const audioElement = document.getElementById(`remote-audio-${i}`);
            if (videoElement && trackState.videoTrack) {
                const stream = videoElement.srcObject as MediaStream | null;
                const hasActiveVideo = stream?.getVideoTracks().some(t => t.readyState === 'live') ?? false;
                if (!hasActiveVideo) {
                    trackState.videoTrack.attach(videoElement);
                }
            }
            if (audioElement && trackState.audioTrack) {
                const stream = (audioElement as HTMLMediaElement).srcObject as MediaStream | null;
                const hasActiveAudio = stream?.getAudioTracks().some(t => t.readyState === 'live') ?? false;
                if (!hasActiveAudio) {
                    trackState.audioTrack.attach(audioElement);
                }
            }
        }

        // 3. 아직 할당되지 않은 트랙 찾기 (우선순위 순)
        const assignedTrackIds = new Set(remoteCellId.current.filter((id) => id));
        const unassignedTracks = getSortedTracks().filter((t) => !assignedTrackIds.has(t.trackId));

        const isLargeGrid = remoteSize > 50;

        if (isLargeGrid) {
            // 대규모: own(짝)은 앞 절반 셀의 빈 칸에만, bot(홀)은 뒤 절반 셀의 빈 칸에만 채운다.
            // (attachAllTracks와 동일 규칙. 증분에서 경계를 무시하면 own이 뒤쪽까지 번져 색이 섞인다.)
            const ownBoundary = Math.ceil(remoteSize / 2);
            const placeInRange = (which: 'own' | 'bot', start: number, end: number) => {
                const pool = unassignedTracks.filter(
                    (t) => shouldReceiveByName(t.trackState.displayName ?? '', which)
                );
                let idx = 0;
                for (let i = start; i < end && idx < pool.length; i++) {
                    if (remoteCellId.current[i]) continue;
                    const track = pool[idx++];
                    const videoElement = remoteElemRef.current?.[i];
                    const audioElement = document.getElementById(`remote-audio-${i}`);
                    if (videoElement && track.trackState.videoTrack) {
                        track.trackState.videoTrack.attach(videoElement);
                    }
                    if (audioElement && track.trackState.audioTrack) {
                        track.trackState.audioTrack.attach(audioElement);
                    }
                    remoteCellId.current[i] = track.trackId;
                    hiddenTrackMaps.current.delete(track.trackId);
                }
                // 남은 pool은 hidden pool로 (로테이션 대상)
                for (; idx < pool.length; idx++) {
                    hiddenTrackMaps.current.set(pool[idx].trackId, pool[idx].priority);
                }
            };
            placeInRange('own', 0, ownBoundary);
            placeInRange('bot', ownBoundary, remoteSize);
        } else {
            // 4. 빈 셀에 새 트랙 할당
            let trackIdx = 0;
            for (let i = 0; i < remoteSize && trackIdx < unassignedTracks.length; i++) {
                if (!remoteCellId.current[i]) {
                    const track = unassignedTracks[trackIdx];
                    const videoElement = remoteElemRef.current?.[i];
                    const audioElement = document.getElementById(`remote-audio-${i}`);
                    if (videoElement && track.trackState.videoTrack) {
                        track.trackState.videoTrack.attach(videoElement);
                    }
                    if (audioElement && track.trackState.audioTrack) {
                        track.trackState.audioTrack.attach(audioElement);
                    }
                    remoteCellId.current[i] = track.trackId;
                    hiddenTrackMaps.current.delete(track.trackId);
                    trackIdx++;
                }
            }

            // 5. 나머지 트랙은 hidden pool 로 (로테이션 대상)
            for (; trackIdx < unassignedTracks.length; trackIdx++) {
                const track = unassignedTracks[trackIdx];
                hiddenTrackMaps.current.set(track.trackId, track.priority);
            }
        }

        syncDisplayedParticipants();
        onConstraintUpdate?.();
        forceUpdate();
    }, [remoteTracksArray, remoteSize, getSortedTracks, syncDisplayedParticipants, onConstraintUpdate]);

    // 레이아웃 변경 시 remoteSize 가 바뀌면 전체 재부착 필요
    const prevRemoteSizeRef = useRef(remoteSize);
    useEffect(() => {
        if (prevRemoteSizeRef.current !== remoteSize) {
            // 기존 셀에서 트랙 분리
            // DOM에서 직접 조회하여 언마운트된 element에 대한 stale ref 접근 방지
            if (remoteTracks.current) {
                const oldSize = prevRemoteSizeRef.current;
                for (let i = 0; i < oldSize; i++) {
                    const trackId = remoteCellId.current[i];
                    if (!trackId) continue;
                    const trackState = remoteTracks.current.get(trackId);
                    if (!trackState) continue;
                    const videoElement = document.getElementById(`remote-video-${i}`) as HTMLVideoElement | null;
                    const audioElement = document.getElementById(`remote-audio-${i}`) as HTMLMediaElement | null;
                    if (videoElement && trackState.videoTrack) {
                        trackState.videoTrack.detach(videoElement);
                    }
                    if (audioElement && trackState.audioTrack) {
                        trackState.audioTrack.detach(audioElement);
                    }
                }
            }
            // 축소 시 stale ref 엔트리 정리
            if (remoteElemRef.current && remoteSize < prevRemoteSizeRef.current) {
                remoteElemRef.current.length = remoteSize;
            }
            prevRemoteSizeRef.current = remoteSize;
            remoteCellId.current = Array(remoteSize).fill('');
            hiddenTrackMaps.current.clear();
            priorityCache.current.clear();
            displayedParticipants?.current?.clear();
            excludedParticipants?.current?.clear();
            // 레이아웃 변경 시 안정화 기간 재시작 (JVB ramp-up 대기)
            isStabilizingRef.current = true;
            setTimeout(() => {
                isStabilizingRef.current = false;
                console.log('🟢 [STABILIZE] 레이아웃 변경 안정화 종료 - exclude 평가 재개');
            }, STABILIZATION_PERIOD);
            isFirstAttachRef.current = true;
        }
    }, [remoteSize]);

    // 트랙 부착: 첫 렌더링 시 전체, 이후 증분 업데이트
    useEffect(() => {
        if (isFirstAttachRef.current) {
            isFirstAttachRef.current = false;
            attachAllTracks();
        } else {
            incrementalAttach();
        }
    }, [attachAllTracks, incrementalAttach]);

    // studio_state 변경 시(selected/ban) 즉시 전체 재배치
    const studioStateVersionRef = useRef(0);
    useEffect(() => {
        if (studioStateVersion === 0 || studioStateVersion === studioStateVersionRef.current) return;
        studioStateVersionRef.current = studioStateVersion;
        attachAllTracks();
    }, [studioStateVersion, attachAllTracks]);

    // 로테이션 인터벌 (remoteSize 변경 시 재시작하여 새 레이아웃 안정화 시간 확보)
    useEffect(() => {
        rotationIntervalRef.current = setInterval(() => {
            latestFnsRef.current.rotationByPolicy();
            latestFnsRef.current.cleanupCache();
            (latestFnsRef.current as any).diagnoseCells?.('로테이션');
        }, ROTATION_INTERVAL);
        return () => {
            if (rotationIntervalRef.current) {
                clearInterval(rotationIntervalRef.current);
                rotationIntervalRef.current = null;
            }
        };
    }, [remoteSize]);

    // 초기 안정화: bridge channel open + JVB forwarding 안정화 대기
    useEffect(() => {
        isStabilizingRef.current = true;
        const timer = setTimeout(() => {
            isStabilizingRef.current = false;
            console.log('🟢 [STABILIZE] 안정화 기간 종료 - exclude 평가 시작');
        }, STABILIZATION_PERIOD);
        return () => clearTimeout(timer);
    }, []);

    // 언마운트 시 전체 정리
    useEffect(() => {
        return () => {
            latestFnsRef.current.detachAllTracks();
        };
    }, []);

    return (
        <>
            {Array.from({length: remoteSize}).map((_, index) => {
                const trackId = remoteCellId.current[index];
                const cellName = trackId ? getDisplayName(trackId) : '-';
                return (
                    <div
                        id={`remote-cell-${index}`}
                        style={{
                            width: '100%',
                            padding: '0',
                            position: 'relative',
                            backgroundColor: '#1c1c1c',
                            overflow: 'hidden',
                            justifySelf: `${gridInfo.remoteJustifySelf}`,
                            backgroundImage: `url(${CenterWait})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                        }}
                        key={index}
                    >
                        <video
                            id={`remote-video-${index}`}
                            autoPlay={true}
                            muted={true}
                            playsInline={true}
                            style={{
                                display: 'block',
                                objectFit: 'cover',
                                padding: '0',
                                width: '100%',
                                height: '100%',
                                aspectRatio: 16 / 9,
                            }}
                            crossOrigin="anonymous"
                            ref={(el) => {
                                if (remoteElemRef.current) {
                                    remoteElemRef.current[index] = el as HTMLVideoElement;
                                }
                            }}
                        />
                        <audio id={`remote-audio-${index}`} autoPlay={true} />
                        {gridLayout.sub ? (
                            <div
                                className="participant-name"
                                style={{
                                    maxWidth: '95%',
                                    position: 'absolute',
                                    bottom: '0',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    fontSize: '1rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    padding: '0.1rem',
                                }}
                            >
                                {!isWatching && cellName === '미개통 경로당'
                                    ? tempCenterName[index % tempCenterName.length]
                                    : cellName}
                            </div>
                        ) : (
                            <div></div>
                        )}
                    </div>
                );
            })}
        </>
    );
};

export default RemoteCell;
